import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { FeatureToggleService, LineOfBusiness, ProductNames, TenantNames } from '@mynexus/mynexus-ui-lib';
import { SendFaxBaseService } from '@core/services/send-fax/send-fax-base.service';
import { DocumentFileType, ICoreMemberDTO } from '@app/core/model';
import { IDocumentResponseDTO } from '@products/dmepos/models';
import { PlatformService } from '@app/core/services/context/platform.service';
import { DocumentTypesList, ProviderTypes } from '@app/documents/models';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { PACOutboundFaxDTO } from '../../models/pacOutboundFaxDTO';
import { noteTypesConfig } from '../../components/letters/denc-letter/denc-letter.component';
import { map, Observable, switchMap } from 'rxjs';
import { PacmaDocuments } from './../../constants/document.constants';
import { DocumentSource, DocumentTypeCodes } from '@app/core/enumerations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Injectable({
  providedIn: 'root'
})
export class SendFaxPacService extends SendFaxBaseService {
  documentTypeConfig = null;
  private readonly platformService = inject(PlatformService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly destroyRef = inject(DestroyRef);
  private pacProductAndLobMigration = false;

  constructor(injector: Injector) {
    super(injector);
    
    this.featureToggleService.getFeatureAccess('PacProductAndLobMigration')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.pacProductAndLobMigration = isEnabled;
      });
  }

  private getBaseUrl(tenantId: string, id: string): string {
    return `${this.apiUrl}/tenants/${tenantId}/authorizations/${id}`;
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  sendLetter(payload: any, authorizationId: string, tenantId?: string) {
    return this.http.post<boolean>(`${this.getBaseUrl(tenantId, authorizationId)}/Letter/CreateDocument`, payload);
  }
 // eslint-disable-next-line @typescript-eslint/no-empty-function
  sendFax(payload: PACOutboundFaxDTO | any, authorizationId: string, tenantId: string) {
    const url = `${this.apiUrl}/tenants/${tenantId}/Fax/`;
    payload.clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    payload.authNumber = payload.authorizationNumber;
    payload.tenantId = tenantId;
    payload.benefitPlanId = '';
    payload.healthPlanId = tenantId;
    payload.authNumber = payload.authorizationNumber;
    payload.createdBy = this.authenticationService.userEmail as string;
    payload.fileDescription = this.getFileDescription(payload);
    payload.attachedDocMetaDetaIds =payload.attachedDocMetaDataIds;
    const isPacMA = this.pacProductAndLobMigration 
      ? this.platformService.widgetContext === ProductNames.PACIM && this.platformService?.lineOfBusiness == LineOfBusiness.MedicareAdvantage 
      : this.platformService.widgetContext === ProductNames.PACIM;
    if(isPacMA && !PacmaDocuments.includes(payload.documentType))
    {
      return this.http.post<any>(url + 'SendFax', payload)
    } else{
      const lineIds = [];
      payload.serviceLinesData?.forEach(x => {
        lineIds.push(x.AuthorizationDetailId);
      });
      payload.fileType = payload.documentDisplayName;
      payload.lineIds = lineIds;
      return this.http.post<any>(url + 'CreateFax', payload)
      }
  }

  getDocumentTypes(authorization: any, tenantName?: TenantNames, memberDto?: ICoreMemberDTO): Observable<DocumentFileType[]> | any {
    const isWellpoint = tenantName === TenantNames.Wellpoint;
    const programState = memberDto?.streamData?.['programState']?.trim()?.toUpperCase();  
    return this.featureToggleService.getFeatureAccess('PacProductAndLobMigration').pipe(
      switchMap(pacProductAndLobMigration => {
        const isPacCommercial = pacProductAndLobMigration && (pacProductAndLobMigration 
          ? this.platformService.widgetContext === ProductNames.PACIM && this.platformService?.lineOfBusiness == LineOfBusiness.Commercial
          : this.platformService.widgetContext === ProductNames.PacCommercial);
        
        return this.http.get<any>(this.apiUrl + `/tenants/${this.shellService.getTenantId()}/authorizations/${authorization.authorizationByProductId}/Documents/GetDocumentTypes?facilityType=${authorization.facilityType.description}`)
          .pipe(map((types: DocumentFileType[] | any) => {
            if (isPacCommercial && isWellpoint && programState === 'WA') {
              return types;
            } else {
              return types.filter(doc => doc.documentTypeCode !== DocumentTypeCodes.WrittenAcknowledgement);
            }
          }));
      })
    );
  }

  override filterDocuments(documents: IDocumentResponseDTO[]): IDocumentResponseDTO[] {
    return documents.filter(document => document.documentSource !== DocumentSource.Appeals);
  }

  handleDocuments(documents: any, payload: any): any {
    if (documents && documents.length > 0) {
      // according to previous logic we need to get name of last item
      const lastDocument = documents.slice(-1).pop();

      payload.documentFileName = lastDocument ? lastDocument.documentFileName : '';
      payload.attachedDocMetaDataIds = documents.map(d => d.docMetaDataId);
    } else {
      payload.attachedDocMetaDataIds = [];
    }
    return payload;
  }

 getFileDescription(payload: any): string {

    if ( payload.documentDisplayName && (payload.documentDisplayName === DocumentTypesList.ITDDenialNotification ||
      payload.documentDisplayName === DocumentTypesList.DenialNotification ||
      payload.documentDisplayName === DocumentTypesList.AdditionalInformationFax ||
      payload.documentDisplayName === DocumentTypesList.AuthorizationFax ||
      payload.documentDisplayName === DocumentTypesList.AuthorizationLetter ||
      payload.documentDisplayName === DocumentTypesList.P2POffer ||
      payload.documentDisplayName === DocumentTypesList.FacilityDenialNotification|| 
      payload.documentDisplayName === DocumentTypesList.WrittenAcknowledgementFax)) {
      payload.fileDescription = (payload.providerType === ProviderTypes.DMEPOSProvider) ?
        'Servicing Provider Notification' :
        'Ordering Provider Notification';
    } else if (payload.documentDisplayName && (payload.documentDisplayName === DocumentTypesList.ExpeditedGrievanceLetter ||
      payload.documentDisplayName === DocumentTypesList.ApprovalLetter ||
      payload.documentDisplayName === DocumentTypesList.DenialLetter ||
      payload.documentDisplayName === DocumentTypesList.Dismissal) ) {
      payload.fileDescription = ""
      }
    else {
      payload.fileDescription = payload.documentType
    }
    return payload.fileDescription;
  }

  getAuthNotesByTypes(authorizationNumber:string, noteTypesConfigList: noteTypesConfig[] ) : Observable<any> {
    return this.http.post<any>( this.apiUrl + `/${this.shellService.getTenantId()}/notes/authorization-number/${authorizationNumber}`, noteTypesConfigList);
  }
}
